import { Link } from "react-router-dom";
import { NerdStackFooter } from "../components/NerdStackFooter.jsx";
import { ToolCard } from "../components/ToolCard.jsx";
import { ToolGrid } from "../components/ToolGrid.jsx";
import BackArrow from "../components/BackArrow.jsx";
import { GamesShell, GAMES_ACCENT } from "./GamesShell.jsx";
import { FAMILY_LIST } from "./families.js";
import { GAMES, gamesInFamily } from "./registry.js";

// V45.3 — GamesHome is now a custom ChemHome-style layout (not a GamesShell wrapper).
const EM = GAMES_ACCENT;     // pink #EC4899
const EM_DEEP = "#7C3AED";   // family violet
const EM_LIGHT = "#F5A8C8";  // light pink for gradient-text

const rgba = (hex, a) => {
  const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
};

// GamesLanding — preserved verbatim from pre-V45.3.
export function GamesLanding() {
  return (
    <div style={{ minHeight: "100vh", background: "#0A0B14", color: "#E8E9F3", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px", textAlign: "center", paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>
      <div style={{ width: 72, height: 72, borderRadius: 18, background: `linear-gradient(135deg, ${GAMES_ACCENT}, ${EM_DEEP})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 30, fontWeight: 700, color: "#fff", marginBottom: 20 }}>
        G
      </div>
      <h1 style={{ fontSize: 26, fontWeight: 700, margin: "0 0 8px" }}>NerdGames</h1>
      <p style={{ fontSize: 15, color: "#9A9DBE", margin: "0 0 28px", maxWidth: 340 }}>
        Leveled mastery games — learn a subject by depth, one stage at a time.
        Free. Fast. Actually good.
      </p>
      <Link to="/games/home" style={{ background: GAMES_ACCENT, color: "#2A0A1B", fontWeight: 600, fontSize: 15, padding: "13px 28px", borderRadius: 12, textDecoration: "none" }}>
        Open NerdGames
      </Link>
    </div>
  );
}

export function GamesHome() {
  return (
    <div style={{ minHeight: "100vh", background: "#0A0B14", fontFamily: "'DM Sans', system-ui, sans-serif", color: "#E2E8F0" }}>
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, height: "70%", background: `radial-gradient(ellipse at center bottom, ${rgba(EM, 0.18)} 0%, transparent 70%)`, pointerEvents: "none" }} />

      <header style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "16px 24px",
        paddingTop: "calc(16px + env(safe-area-inset-top))",
        paddingLeft: "calc(24px + env(safe-area-inset-left))",
        paddingRight: "calc(24px + env(safe-area-inset-right))",
        position: "relative", zIndex: 10,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <BackArrow to="/" from={EM} toColor={EM_LIGHT} />
          <div style={{ width: 38, height: 38, borderRadius: 11, background: `linear-gradient(135deg, ${EM}, ${EM_DEEP})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, fontWeight: 800, color: "#fff" }}>G</div>
          <svg width="115" height="24" viewBox="0 0 115 24" xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
            <defs>
              <linearGradient id="wm-games" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor={EM_LIGHT} />
                <stop offset="1" stopColor="#F5A8C8" />
              </linearGradient>
            </defs>
            <text x="0" y="19" fill="url(#wm-games)" fontFamily="DM Sans, system-ui, sans-serif" fontSize="20" fontWeight="800" letterSpacing="-0.5">NerdGames</text>
          </svg>
        </div>
      </header>

      <main style={{ maxWidth: 820, margin: "0 auto", padding: "40px 20px 80px", position: "relative", zIndex: 10 }}>
        <h1 style={{ fontSize: "clamp(32px, 6vw, 52px)", fontWeight: 800, letterSpacing: "-1.5px", textAlign: "center", margin: "0 0 14px", lineHeight: 1.1 }}>
          Learning games.<br /><span style={{ background: `linear-gradient(135deg, ${EM_LIGHT}, ${EM})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Free. Fast. Actually good.</span>
        </h1>
        <p style={{ fontSize: 17, color: "#9CA3AF", textAlign: "center", maxWidth: 580, margin: "0 auto 14px", lineHeight: 1.6 }}>
          Games that teach what they pretend to teach. Twenty games across math, chem, bio, and physics — build atoms, bond molecules, solve pedigrees, find ORFs. No flashcards reskinned as games.
        </p>
        <p style={{ fontSize: 15, color: "#6F7290", textAlign: "center", maxWidth: 580, margin: "0 auto 44px", lineHeight: 1.6 }}>
          Free for students. No signup. No tracking.
        </p>

        <p style={{ fontSize: 14, color: "#9A9DBE", textAlign: "center", margin: "0 0 22px", lineHeight: 1.55 }}>
          Pick a family to browse, or scroll down to see every game.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 14, marginBottom: 36 }}>
          {FAMILY_LIST.map(f => {
            const count = gamesInFamily(f.id).length;
            return (
              <Link key={f.id} to={f.homePath} style={{
                background: `linear-gradient(135deg, ${f.accent}22, ${f.accentDeep}11)`,
                border: `1px solid ${f.accent}55`, borderRadius: 14, padding: "20px 18px",
                textDecoration: "none", color: "#E8E9F3",
                display: "flex", flexDirection: "column", gap: 6, minHeight: 110,
              }}>
                <div style={{ fontSize: 11, color: f.accent, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>
                  {count} {count === 1 ? "game" : "games"}
                </div>
                <div style={{ fontSize: 19, fontWeight: 600, marginTop: 2 }}>{f.name}</div>
                <div style={{ fontSize: 12.5, color: "#9A9DBE", lineHeight: 1.45 }}>{f.description}</div>
              </Link>
            );
          })}
        </div>
        <details style={{ marginTop: 8 }}>
          <summary style={{ cursor: "pointer", fontSize: 13, color: "#9A9DBE", marginBottom: 12, listStyle: "none", padding: "10px 0", borderTop: "1px solid #1C1E2E" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 10 }}>▶</span>
              Browse all {GAMES.length} games
            </span>
          </summary>
          <ToolGrid>
            {GAMES.map(g => (
              <ToolCard key={g.id} accent={GAMES_ACCENT} to={g.path} name={g.name} desc={g.tagline} />
            ))}
          </ToolGrid>
        </details>
      </main>

      <footer style={{ textAlign: "center", padding: 24, fontSize: 12, color: "#1F2937", borderTop: `1px solid ${rgba(EM, 0.04)}` }}>
        <NerdStackFooter variant="tool" family="games" />
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        body { margin: 0; }
      `}</style>
    </div>
  );
}
